import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faTruck, faBoxOpen, faExchangeAlt, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context'; // Add this import
import Loader from '../Loader';
import './ProductPage.css';

const ProductPage = ({ products }) => {
  const { id } = useParams();
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [comment, setComment] = useState('');
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    // In a real app, this would be an API call
    const foundProduct = products.find(p => p.id === parseInt(id));
    setProduct(foundProduct);
    if (foundProduct) {
      setActiveImage(foundProduct.imageUrl);
    }
  }, [id, products]);

  if (!product) {
    return <Loader fullPage size="large" />;
  }

  const handleAddToCart = () => {
    if (product.details?.sizes && !selectedSize) {
      alert('Please select a size');
      return false;
    }

    const itemToAdd = {
      id: product.id,
      name: product.name,
      price: product.isOnSale ? product.salePrice : product.price,
      quantity: quantity,
      size: selectedSize,
      comment: comment,
      imageUrl: product.imageUrl
    };

    console.log(itemToAdd)
    addToCart(itemToAdd);
    return true;
  };

  const handleBuyNow = () => {
    if (handleAddToCart()) {
      navigate('/checkout');
    }
  };


  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  return (
    <div className="product-page">
      <div className="product-container">
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <span className="separator"></span>
          <Link to="/shop">Shop</Link>
          <span className="separator"></span>
          <Link to={`/shop/${product.category}`}>{product.category}</Link>
          <span className="separator"></span>
          <span className="current">{product.name}</span>
        </nav>

        <div className="product-grid">
          <div className="product-gallery">
            <div className="main-image">
              <img src={activeImage || product.imageUrl} alt={product.name} />
            </div>
            {product.images && product.images.length > 0 && (
              <div className="thumbnail-list">
                {product.images.map((img, index) => (
                  <div 
                    key={index} 
                    className={`thumbnail ${activeImage === img ? 'active' : ''}`}
                    onClick={() => setActiveImage(img)}
                  >
                    <img src={img} alt={`${product.name} thumbnail ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="product-info-sticky">
            <div className="product-info">
              <h1>{product.name}</h1>
              
              <div className="product-price">
                {product.isOnSale ? (
                  <>
                    <span className="sale-price">${product.salePrice}</span>
                    <span className="original-price">${product.price}</span>
                  </>
                ) : (
                  <span>${product.price}</span>
                )}
              </div>

              {product.category === 'preservation' && (
                <div className="product-subheading">
                  <p>Learn more about <Link to="/expectations">what to expect</Link> with your preservation process <Link to="/expectations">here.</Link></p>
                </div>
              )}

              <p className="product-description">{product.description}</p>

              {product.details?.sizes && (
                <div className="size-selector">
                  <h3 className="section-label">Select Size</h3>
                  <div className="size-buttons">
                    {product.details.sizes.map(size => (
                      <button
                        key={size}
                        className={`size-button ${selectedSize === size ? 'selected' : ''}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="purchase-options">
                <div className="quantity-selector">
                  <h3 className="section-label">Quantity</h3>
                  <div className="quantity-controls">
                    <button onClick={() => handleQuantityChange(-1)} disabled={quantity === 1}>
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button onClick={() => handleQuantityChange(1)} disabled={quantity === 10}>
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>

                <div className="action-buttons">
                  <button className="add-to-cart-btn" onClick={handleAddToCart}>Add to Cart</button>
                  <button className="buy-now-btn" onClick={handleBuyNow}>
                    Buy it now
                  </button>
                </div>
              </div>

              <div className="comment-section">
                <h3 className="section-label">Add a comment (optional)</h3>
                <textarea
                  className="comment-textarea"
                  placeholder="Any special instructions?"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="3"
                />
              </div>

              <div className="shipping-features">
                <div className="feature">
                  <FontAwesomeIcon icon={faTruck} />
                  <span>Free Shipping on Orders $50+</span>
                </div>
                <div className="feature">
                  <FontAwesomeIcon icon={faBoxOpen} />
                  <span>Easy 30-Day Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="product-details-extra">
          <div className="details-grid">
            {product.details && (
              <>
                <div className="details-section">
                  <h3 className="section-title">Details</h3>
                  {product.details.material && <p><strong>Material:</strong> {product.details.material}</p>}
                  {product.details.dimensions && <p><strong>Dimensions:</strong> {product.details.dimensions}</p>}
                  {product.details.care && (
                    <div className="care-section">
                      <strong>Care:</strong>
                      <p>{product.details.care}</p>
                    </div>
                  )}
                </div>

                <div className="details-section">
                  <h3 className="section-title">Features</h3>
                  <ul className="features-list">
                    {product.details.features?.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
